// Real gRPC-Web service wrapper for Nockchain API using direct fetch for reliability
// Import JavaScript protobuf classes
const nockchain_pb = require('../proto/nockchain/public/v2/nockchain_pb.js');
const common_pb = require('../proto/nockchain/common/v1/primitives_pb.js');
const pagination_pb = require('../proto/nockchain/common/v1/pagination_pb.js');

const BASE_URL = 'https://rpc.nockchain.net';

// Network timeout configurations
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const LONG_TIMEOUT = 60000; // 60 seconds for heavy operations

// Error types
export class GrpcError extends Error {
  constructor(
    message: string,
    public code: number,
    public details?: any
  ) {
    super(message);
    this.name = 'GrpcError';
  }
}

export class NockchainGrpcService {
  // gRPC(-web) message framing: 1 byte flags + 4 byte big-endian length + message bytes
  private static frameRequest(messageBytes: Uint8Array): Uint8Array {
    const len = messageBytes.length >>> 0;
    const framed = new Uint8Array(5 + len);
    framed[0] = 0x00; // not compressed
    framed[1] = (len >>> 24) & 0xff;
    framed[2] = (len >>> 16) & 0xff;
    framed[3] = (len >>> 8) & 0xff;
    framed[4] = len & 0xff;
    framed.set(messageBytes, 5);
    return framed;
  }

  private static parseGrpcWebUnaryResponse(
    url: string,
    responseBytes: Uint8Array,
    responseType: any
  ): any {
    // gRPC-Web response can contain multiple frames:
    // - data frames: flags 0x00 or 0x01 (compressed)
    // - trailers frame: flags 0x80, payload is ASCII headers block (grpc-status, grpc-message, etc)
    let offset = 0;
    let firstMessage: any | null = null;
    let trailersText: string | null = null;

    const decodeMessage = (payload: Uint8Array) => {
      // google-protobuf JS codegen typically provides a *static* deserializeBinary(bytes)
      if (responseType && typeof responseType.deserializeBinary === 'function') {
        return responseType.deserializeBinary(payload);
      }
      // Some codegens provide an instance method
      const msg = new responseType();
      if (msg && typeof msg.deserializeBinary === 'function') {
        msg.deserializeBinary(payload);
        return msg;
      }
      throw new GrpcError('Response type cannot deserializeBinary', -1, {
        url,
        responseType: String(responseType),
      });
    };

    while (offset + 5 <= responseBytes.length) {
      const flags = responseBytes[offset];
      const len =
        (responseBytes[offset + 1] << 24) |
        (responseBytes[offset + 2] << 16) |
        (responseBytes[offset + 3] << 8) |
        responseBytes[offset + 4];
      offset += 5;

      if (offset + len > responseBytes.length) {
        throw new GrpcError('Invalid gRPC response: incomplete frame', -1, {
          url,
          expected: offset + len,
          actual: responseBytes.length,
        });
      }

      const payload = responseBytes.slice(offset, offset + len);
      offset += len;

      // trailers frame
      if ((flags & 0x80) === 0x80) {
        trailersText = new TextDecoder().decode(payload);
        continue;
      }

      // data frame
      if ((flags & 0x01) === 0x01) {
        // compressed bit set
        throw new GrpcError(
          `protocol error: received message with invalid compression flag: ${flags} (valid flags are 0 and 1)`,
          -1,
          { url, flags }
        );
      }

      if (!firstMessage) {
        const msg = decodeMessage(payload);
        firstMessage = typeof msg?.toObject === 'function' ? msg.toObject() : msg;
      }
    }

    // If we got trailers, try to surface grpc-status/grpc-message even if there was no message.
    if (trailersText) {
      const lines = trailersText.split(/\r?\n/).filter(Boolean);
      const map: Record<string, string> = {};
      for (const line of lines) {
        const idx = line.indexOf(':');
        if (idx === -1) continue;
        const k = line.slice(0, idx).trim().toLowerCase();
        const v = line.slice(idx + 1).trim();
        map[k] = v;
      }
      if (map['grpc-status'] && map['grpc-status'] !== '0') {
        throw new GrpcError(
          map['grpc-message']
            ? decodeURIComponent(map['grpc-message'])
            : `gRPC error status: ${map['grpc-status']}`,
          parseInt(map['grpc-status'], 10),
          { url, trailers: map }
        );
      }
    }

    return firstMessage ?? {};
  }

  private static async makeUnaryCall(
    fullUrl: string,
    requestMessage: any,
    responseType: any,
    timeout: number = DEFAULT_TIMEOUT
  ): Promise<any> {
    // Use fetch API directly to ensure requests go to the correct URL
    const requestData = NockchainGrpcService.frameRequest(
      requestMessage.serializeBinary()
    );
    const url = fullUrl;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/grpc-web+proto',
          'X-Grpc-Web': '1',
          'Accept': 'application/grpc-web+proto'
        },
        body: requestData as unknown as BodyInit,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new GrpcError(
          `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          { url, status: response.status }
        );
      }

      const buffer = await response.arrayBuffer();
      const uint8Array = new Uint8Array(buffer);

      // Parse gRPC-Web frames (data + trailers)
      return NockchainGrpcService.parseGrpcWebUnaryResponse(
        url,
        uint8Array,
        responseType
      );
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new GrpcError(
          `Request timeout after ${timeout}ms`,
          4, // DEADLINE_EXCEEDED
          { url, timeout }
        );
      }

      if (error instanceof GrpcError) {
        throw error;
      }

      throw new GrpcError(
        error.message || 'Network error',
        -1,
        { url, originalError: error }
      );
    }
  }

  static async walletGetBalance(request: {
    address?: string;
    firstName?: string;
    page?: {
      clientPageItemsLimit?: number;
      pageToken?: string;
      maxBytes?: number;
    };
  }): Promise<any> {
    const protoRequest = new nockchain_pb.WalletGetBalanceRequest();

    if (request.address) {
      const address = new common_pb.Base58Pubkey();
      address.setKey(request.address);
      protoRequest.setAddress(address);
    } else if (request.firstName) {
      const firstName = new common_pb.Base58Hash();
      firstName.setHash(request.firstName);
      protoRequest.setFirstName(firstName);
    }

    // Page request is required - always create one
    const pageRequest = new pagination_pb.PageRequest();
    if (request.page) {
      if (request.page.clientPageItemsLimit) {
        pageRequest.setClientPageItemsLimit(request.page.clientPageItemsLimit);
      }
      if (request.page.pageToken) {
        pageRequest.setPageToken(request.page.pageToken);
      }
      if (request.page.maxBytes) {
        pageRequest.setMaxBytes(request.page.maxBytes);
      }
    }
    // Set default values if not provided
    if (!pageRequest.getClientPageItemsLimit()) {
      pageRequest.setClientPageItemsLimit(50); // Default page size
    }
    protoRequest.setPage(pageRequest);

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainService/WalletGetBalance',
      protoRequest,
      nockchain_pb.WalletGetBalanceResponse
    );
  }

  static async walletSendTransaction(request: {
    txId: string;
    rawTx: any; // This would be a properly structured RawTransaction
  }): Promise<any> {
    const protoRequest = new nockchain_pb.WalletSendTransactionRequest();

    const hash = new common_pb.Hash();
    // For now, we'll set a placeholder hash - in real implementation,
    // this would be properly constructed from the txId
    protoRequest.setTxId(hash);

    // For now, we'll skip the raw transaction construction
    // In a real implementation, you'd construct the RawTransaction protobuf

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainService/WalletSendTransaction',
      protoRequest,
      nockchain_pb.WalletSendTransactionResponse
    );
  }

  static async transactionAccepted(request: {
    txId: string;
  }): Promise<any> {
    const protoRequest = new nockchain_pb.TransactionAcceptedRequest();

    const txId = new common_pb.Base58Hash();
    txId.setHash(request.txId);
    protoRequest.setTxId(txId);

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainService/TransactionAccepted',
      protoRequest,
      nockchain_pb.TransactionAcceptedResponse
    );
  }

  static async getBlocks(request: {
    page?: {
      clientPageItemsLimit?: number;
      pageToken?: string;
      maxBytes?: number;
    };
  } = {}): Promise<any> {
    const protoRequest = new nockchain_pb.GetBlocksRequest();

    // Page request is required - always create one
    const pageRequest = new pagination_pb.PageRequest();
    if (request.page) {
      if (request.page.clientPageItemsLimit) {
        pageRequest.setClientPageItemsLimit(request.page.clientPageItemsLimit);
      }
      if (request.page.pageToken) {
        pageRequest.setPageToken(request.page.pageToken);
      }
      if (request.page.maxBytes) {
        pageRequest.setMaxBytes(request.page.maxBytes);
      }
    }
    // Set default values if not provided
    if (!pageRequest.getClientPageItemsLimit()) {
      pageRequest.setClientPageItemsLimit(50); // Default page size
    }
    protoRequest.setPage(pageRequest);

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainBlockService/GetBlocks',
      protoRequest,
      nockchain_pb.GetBlocksResponse,
      LONG_TIMEOUT // Blocks can take longer to fetch
    );
  }

  static async getBlockDetails(request: {
    height?: number;
    blockId?: string;
  }): Promise<any> {
    const protoRequest = new nockchain_pb.GetBlockDetailsRequest();

    if (request.height !== undefined) {
      protoRequest.setHeight(request.height);
    } else if (request.blockId) {
      protoRequest.setBlockId(request.blockId);
    }

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainBlockService/GetBlockDetails',
      protoRequest,
      nockchain_pb.GetBlockDetailsResponse,
      LONG_TIMEOUT
    );
  }

  static async getTransactionBlock(request: {
    txId: string;
  }): Promise<any> {
    const protoRequest = new nockchain_pb.GetTransactionBlockRequest();

    const txId = new common_pb.Base58Hash();
    txId.setHash(request.txId);
    protoRequest.setTxId(txId);

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainBlockService/GetTransactionBlock',
      protoRequest,
      nockchain_pb.GetTransactionBlockResponse
    );
  }

  static async getTransactionDetails(request: {
    txId: string;
  }): Promise<any> {
    const protoRequest = new nockchain_pb.GetTransactionDetailsRequest();

    const txId = new common_pb.Base58Hash();
    txId.setHash(request.txId);
    protoRequest.setTxId(txId);

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainBlockService/GetTransactionDetails',
      protoRequest,
      nockchain_pb.GetTransactionDetailsResponse
    );
  }

  static async getExplorerMetrics(): Promise<any> {
    const protoRequest = new nockchain_pb.GetExplorerMetricsRequest();

    return NockchainGrpcService.makeUnaryCall(
      BASE_URL + '/nockchain.public.v2.NockchainMetricsService/GetExplorerMetrics',
      protoRequest,
      nockchain_pb.GetExplorerMetricsResponse
    );
  }
}